import {
  AtomErrors,
  getErrorCodeFromUnknown,
  getErrorMessageFromUnknown,
  getErrorTypeFromUnknown,
  isRecord,
  readUserMessage,
  UNIQUE_VIOLATION_USER_MESSAGE_KEY_PREFIX,
  type UserMessage,
} from "@atoms/atom-types";

export type GraphQLErrorLike = Readonly<{
  message?: string;
  path?: readonly (string | number)[];
  locations?: readonly unknown[];
  extensions?: Readonly<Record<string, unknown>>;
}>;

export class GraphQLRequestError extends Error {
  public readonly name = "GraphQLRequestError";
  public readonly operationLabel: string;
  public readonly graphQLErrorCount: number;
  public readonly graphQLPath?: readonly (string | number)[];
  public readonly sentryEventId?: string;
  public readonly type?: string;
  public readonly debugInfos?: Readonly<{ errorCode?: string }>;
  /** Error class as the backend sent it; absent on older backends. */
  public readonly errorClass?: string;
  /** Reference of the failed request as the backend sent it. */
  public readonly errorRef?: string;
  /**
   * The sentence of a rule error of the project, as the backend sent it and
   * with the limits applied again. Only an error of class `rule` has one.
   */
  public readonly userMessage?: UserMessage;

  /**
   * @param message - Overrides the message of the first error, for callers
   *   that report several errors as one.
   */
  public constructor(
    graphQLErrors: readonly unknown[],
    operationLabel: string,
    message?: string,
  ) {
    const primaryError = graphQLErrors[0];

    super(
      message ??
        getErrorMessageFromUnknown(primaryError) ??
        `${operationLabel} GraphQL request failed`,
    );
    this.operationLabel = operationLabel;
    this.graphQLErrorCount = graphQLErrors.length;

    const errorCode = getServerErrorCode(primaryError);
    const errorType = getErrorTypeFromUnknown(primaryError);
    const sentryEventId = getSentryEventIdFromGraphQLError(primaryError);
    if (errorType) {
      this.type = errorType;
    }
    if (sentryEventId) {
      this.sentryEventId = sentryEventId;
    }
    if (errorCode) {
      this.debugInfos = { errorCode };
    }
    const errorClass = getWireString(primaryError, "errorClass");
    if (errorClass) {
      this.errorClass = errorClass;
    }
    const errorRef = getWireString(primaryError, "errorRef");
    if (errorRef) {
      this.errorRef = errorRef;
    }
    const userMessage = getWireUserMessage(primaryError);
    if (userMessage) {
      this.userMessage = userMessage;
    }

    const path = getGraphQLErrorPath(primaryError);
    if (path) {
      this.graphQLPath = path;
    }
  }
}

export function throwIfGraphQLErrors(
  errors: readonly unknown[] | undefined,
  operationLabel: string,
): void {
  if (!errors?.length) return;
  throw new GraphQLRequestError(errors, operationLabel);
}

export function toGraphQLRequestError(
  errors: readonly unknown[],
  operationLabel: string,
  message?: string,
): GraphQLRequestError {
  return new GraphQLRequestError(errors, operationLabel, message);
}

export function isLegacyGraphQLErrorResponseObject(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.message === "string" &&
    ("extensions" in value || "locations" in value || "path" in value)
  );
}

export function getSentryEventIdFromUnknown(
  value: unknown,
): string | undefined {
  if (value instanceof GraphQLRequestError) {
    return value.sentryEventId;
  }
  if (!isRecord(value)) return undefined;

  const directEventId = getSentryEventIdFromGraphQLError(value);
  if (directEventId) return directEventId;

  const graphQLErrors = value.graphQLErrors;
  if (Array.isArray(graphQLErrors)) {
    return graphQLErrors
      .map(getSentryEventIdFromGraphQLError)
      .find((eventId) => eventId !== undefined);
  }

  const errors = value.errors;
  if (Array.isArray(errors)) {
    return errors
      .map(getSentryEventIdFromGraphQLError)
      .find((eventId) => eventId !== undefined);
  }

  return undefined;
}

/**
 * The code of a server error. An error the backend classified carries its
 * code in `extensions.errorCode` or has none: nothing is read out of its
 * message, which is server text. Without a class the code is parsed from
 * every shape older backends used.
 */
function getServerErrorCode(value: unknown): string | undefined {
  return getWireString(value, "errorClass")
    ? getWireString(value, "errorCode")
    : getErrorCodeFromUnknown(value);
}

/** A field the backend may add to `extensions`; never required. */
function getWireString(
  value: unknown,
  field: "errorClass" | "errorCode" | "errorRef",
): string | undefined {
  if (!isRecord(value)) return undefined;
  const extensions = value.extensions;
  const wireValue = isRecord(extensions) ? extensions[field] : undefined;
  return typeof wireValue === "string" && wireValue.length > 0
    ? wireValue
    : undefined;
}

/** The class the backend gives a rule error of a project. */
const RULE_ERROR_CLASS = "rule";

/** The class the backend gives a unique violation. */
const UNIQUE_VIOLATION_ERROR_CLASS = "conflict";

/**
 * Whether the backend sent `value` as a unique violation: the code together
 * with its class.
 */
function isWireUniqueViolation(value: unknown): boolean {
  return (
    getWireString(value, "errorClass") === UNIQUE_VIOLATION_ERROR_CLASS &&
    getWireString(value, "errorCode") === AtomErrors.UniqueConstraintViolation
  );
}

/**
 * What a unique violation may bring: the key that names its constraint
 * (`app.errors.unique.<constraint>`) and its parameters. A text is the
 * privilege of a rule error and is discarded here, and so is any other key.
 */
function toUniqueViolationMessage(
  message: UserMessage | undefined,
): UserMessage | undefined {
  const key = message?.key;
  if (
    key === undefined ||
    !key.startsWith(UNIQUE_VIOLATION_USER_MESSAGE_KEY_PREFIX) ||
    key.length === UNIQUE_VIOLATION_USER_MESSAGE_KEY_PREFIX.length
  ) {
    return undefined;
  }
  return message?.params === undefined
    ? { key }
    : { key, params: message.params };
}

/**
 * The user message of a GraphQL error as the backend sent it
 * (`extensions.userMessage`), limited again with `readUserMessage`: the
 * client trusts no part of it. An error of class `rule` has one, and a
 * unique violation (class `conflict`, code `persistence/unique-violation`),
 * whose message is a key without a text. On any other error, and anywhere
 * but in `extensions`, it is ignored.
 */
export function getWireUserMessage(value: unknown): UserMessage | undefined {
  const isRule = getWireString(value, "errorClass") === RULE_ERROR_CLASS;
  if (!isRule && !isWireUniqueViolation(value)) {
    return undefined;
  }
  const extensions = isRecord(value) ? value.extensions : undefined;
  const message = isRecord(extensions)
    ? readUserMessage(extensions.userMessage)
    : undefined;
  return isRule ? message : toUniqueViolationMessage(message);
}

function getGraphQLErrorPath(
  value: unknown,
): readonly (string | number)[] | undefined {
  if (!isRecord(value) || !Array.isArray(value.path)) {
    return undefined;
  }

  return value.path.filter(
    (segment): segment is string | number =>
      typeof segment === "string" || typeof segment === "number",
  );
}

function getSentryEventIdFromGraphQLError(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;

  if (typeof value.sentryEventId === "string") {
    return normalizeSentryEventId(value.sentryEventId);
  }

  const extensions = value.extensions;
  if (isRecord(extensions) && typeof extensions.sentryEventId === "string") {
    return normalizeSentryEventId(extensions.sentryEventId);
  }

  return undefined;
}

function normalizeSentryEventId(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
